/**
 * Floating chat widget — a persistent assistant available on every page.
 *
 * A launcher button fixed to the bottom-right opens an expandable panel that
 * floats over the current page without navigation. The customer can read their
 * dashboard (or any section) and chat at the same time.
 *
 * Design notes:
 *  - Conversation state lives HERE, not in the panel, so it is preserved while
 *    the customer moves between sections and while the panel is closed. It is
 *    kept only for the session and never beyond sign-out (the whole widget is
 *    mounted inside the authenticated shell, so it unmounts on sign-out).
 *  - The panel is kept mounted while open so scroll position and in-flight
 *    requests are stable; it is removed from the DOM when closed.
 *  - Accessibility: the launcher and panel are a labelled disclosure; the panel
 *    is a dialog with a focus trap, ESC to close, focus returned to the
 *    launcher on close, and an aria-live region (inside the panel) for replies.
 *    Respects reduced motion via CSS. On small screens the panel becomes a
 *    near-full-screen sheet (CSS).
 *
 * The widget reuses {@link ChatPanel} (the same thread + input + send logic the
 * `/chat` page uses), so there is one implementation of the conversation.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { ChatTurn } from '../api/chat.js';
import { ChatPanel } from '../views/ChatView.js';
import { IconChat } from './BrandLogo.js';

/** Close (X) glyph for the panel header. */
function CloseIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Selector for the focusable elements the focus trap cycles through. */
const FOCUSABLE =
  'button, [href], textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function ChatWidget(): JSX.Element {
  const [open, setOpen] = useState(false);
  /**
   * Conversation lifted here so it survives closing the panel and changing
   * section. Passed into {@link ChatPanel} as controlled state.
   */
  const [messages, setMessages] = useState<ChatTurn[]>([]);

  const panelRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);

  const panelId = useId();
  const titleId = useId();

  const close = useCallback(() => {
    setOpen(false);
    // Return focus to the launcher for keyboard users.
    launcherRef.current?.focus();
  }, []);

  // ESC closes; Tab is trapped inside the panel while open.
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;

      const panel = panelRef.current;
      if (panel === null) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement;

      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  return (
    <div className="esb-widget">
      {open ? (
        <div
          ref={panelRef}
          className="esb-widget__panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          id={panelId}
        >
          <div className="esb-widget__header">
            <span className="esb-widget__heading">
              <IconChat className="esb-widget__header-icon" />
              <span id={titleId} className="esb-widget__title">
                Assistant
              </span>
            </span>
            <button
              type="button"
              className="esb-widget__close"
              onClick={close}
              aria-label="Close chat"
            >
              <CloseIcon />
            </button>
          </div>

          <ChatPanel
            messages={messages}
            onMessagesChange={setMessages}
            autoFocus
          />
        </div>
      ) : null}

      <button
        ref={launcherRef}
        type="button"
        className="esb-widget__launcher"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? 'Close chat assistant' : 'Open chat assistant'}
      >
        <IconChat className="esb-widget__launcher-icon" />
      </button>
    </div>
  );
}
